import { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import bacService from '../../services/bacStudent.service';
import type { BacSkillChapter } from '../../services/bacStudent.service';

/** Liste des savoir-faire par chapitre > groupe, avec une case « je maîtrise » par savoir-faire. */
export default function SkillsTab({ studentId }: { studentId: number }) {
  const [chapitres, setChapitres] = useState<BacSkillChapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  useEffect(() => {
    bacService
      .getSkills(studentId)
      .then((c) => {
        setChapitres(c);
        const o: Record<string, boolean> = {};
        c.forEach((ch) => {
          o[ch.key] = true;
          ch.groupes.forEach((g) => (o[ch.key + '/' + g.title] = true));
        });
        setOpen(o);
      })
      .catch(() => setError('Erreur lors du chargement des savoir-faire'))
      .finally(() => setLoading(false));
  }, [studentId]);

  const toggle = async (skillId: number, mastered: boolean) => {
    const apply = (value: boolean) =>
      setChapitres((prev) =>
        prev.map((ch) => ({
          ...ch,
          groupes: ch.groupes.map((g) => ({
            ...g,
            skills: g.skills.map((s) => (s.id === skillId ? { ...s, mastered: value } : s)),
          })),
        }))
      );
    apply(mastered);
    try {
      await bacService.setMastery(studentId, skillId, mastered);
    } catch {
      apply(!mastered);
      setError("Impossible d'enregistrer, réessaie.");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Savoir-faire cochés comme maîtrisés (par l'élève ou par vous). L'onglet « Sujets » montre ce que ça rapporte au bac.
      </Typography>
      {chapitres.map((ch) => {
        const all = ch.groupes.flatMap((g) => g.skills);
        const n = all.filter((s) => s.mastered).length;
        return (
          <Accordion
            key={ch.key}
            expanded={!!open[ch.key]}
            onChange={(_, e) => setOpen({ ...open, [ch.key]: e })}
            disableGutters
            sx={{ mb: 1.5 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" pr={1}>
                <Typography fontWeight="bold">{ch.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {n} / {all.length}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {ch.groupes.map((g) => {
                const k = ch.key + '/' + g.title;
                const ng = g.skills.filter((s) => s.mastered).length;
                return (
                  <Accordion
                    key={k}
                    expanded={!!open[k]}
                    onChange={(_, e) => setOpen({ ...open, [k]: e })}
                    disableGutters
                    elevation={0}
                    sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" pr={1}>
                        <Typography color="primary.main" fontWeight={600}>
                          {g.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {ng} / {g.skills.length}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pt: 0 }}>
                      {g.skills.map((s) => (
                        <Box key={s.id} display="flex" alignItems="flex-start" mb={0.5}>
                          {/* rang dans le groupe (1 = le plus rentable), colonne alignée */}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ width: 24, flexShrink: 0, textAlign: 'right', pt: 0.75, pr: 0.5, fontVariantNumeric: 'tabular-nums' }}
                          >
                            {s.rang}
                          </Typography>
                          <FormControlLabel
                            sx={{ display: 'flex', alignItems: 'flex-start', mx: 0, flexGrow: 1 }}
                            control={
                              <Checkbox
                                checked={s.mastered}
                                onChange={(e) => toggle(s.id, e.target.checked)}
                                sx={{ py: 0.5 }}
                              />
                            }
                            label={
                              <Typography variant="body2" sx={{ pt: 0.75, opacity: s.mastered ? 1 : 0.85 }}>
                                {s.name}
                              </Typography>
                            }
                          />
                          {/* centièmes de point par sujet en moyenne */}
                          <Tooltip title="Centièmes de point par sujet, en moyenne sur les sujets de bac de sa série">
                            <Typography
                              variant="body2"
                              sx={{ width: 36, flexShrink: 0, textAlign: 'right', pt: 0.75, fontVariantNumeric: 'tabular-nums' }}
                            >
                              {s.centiemes}
                            </Typography>
                          </Tooltip>
                          {/* pas encore câblé */}
                          <Tooltip title="Je veux travailler sur ça">
                            <IconButton size="small" color="primary" sx={{ ml: 0.5, mt: 0.25, flexShrink: 0 }}>
                              <FitnessCenterIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
